import { getAppContext } from '../hooks/useAppContext';
import { authService } from './auth.service';
/**
 * Lightweight module-level cache for dropdown / master-data API calls.
 *
 * Features:
 *  - TTL-based expiry (default 5 minutes)
 *  - In-flight deduplication — concurrent callers for the same key share one
 *    Promise instead of each firing a separate HTTP request.
 *  - Prefix-based invalidation — call invalidateDropdown('dept:') after any
 *    write to clear all cached department entries.
 */

interface CacheEntry {
  data: unknown;
  expiresAt: number;
}

const store = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<unknown>>();

const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * The context every cached answer belongs to: company, work location and mode.
 *
 * Keys used to be just 'dept:1:100' — the same key for every company on the account. Switch company
 * and, for up to the five-minute TTL, the Department, Designation and Category dropdowns kept
 * serving the PREVIOUS company's master data, because nothing about the key said whose it was. The
 * scope is part of the key now, so two companies cannot collide however long an entry lives.
 *
 * Read from the in-memory context and identity, which is all there is now — nothing about either
 * lives in the browser's storage.
 */
function scope(): string {
  try {
    const ctx = getAppContext();
    const user = authService.getStoredUser();
    return `c${ctx.companyId ?? user?.companyId ?? 0}:u${ctx.branchId ?? 0}:m${user?.loginMode ?? 0}`;
  } catch {
    return 'c0:u0:m0';
  }
}

/**
 * Drop everything. Called when the company, work location, mode or financial year changes: scoping
 * the key already stops the two mixing, and this stops the old company's answers sitting in memory
 * for a switch that will never come back to them.
 */
export function clearDropdownCache(): void {
  store.clear();
  inflight.clear();
}

/**
 * Clear on every context change, from one place.
 *
 * Registered here rather than in each screen because the cache is module-level: whichever component
 * happens to be mounted when the company changes is not the one that owns this, and asking every
 * screen to remember would guarantee that one of them eventually does not.
 */
if (typeof window !== 'undefined') {
  ['company-switched', 'unit-changed', 'mode-switched', 'financial-year-updated']
    .forEach(evt => window.addEventListener(evt, clearDropdownCache));
}

/**
 * Returns cached data if still fresh, otherwise calls `fetcher()`.
 * If another call with the same key is already in-flight, shares that Promise.
 */
export function cachedGet<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttlMs = DEFAULT_TTL_MS,
): Promise<T> {
  const now = Date.now();
  key = `${scope()}|${key}`;
  const hit = store.get(key);
  if (hit && hit.expiresAt > now) return Promise.resolve(hit.data as T);

  const existing = inflight.get(key);
  if (existing) return existing as Promise<T>;

  const promise = fetcher()
    .then((data) => {
      store.set(key, { data, expiresAt: Date.now() + ttlMs });
      inflight.delete(key);
      return data;
    })
    .catch((err) => {
      inflight.delete(key);
      throw err;
    });

  inflight.set(key, promise);
  return promise;
}

/**
 * Removes all cache entries whose key starts with `prefix`.
 * Call this after any create / update / delete mutation.
 */
export function invalidateDropdown(prefix: string): void {
  // Keys carry a scope in front of the prefix now, so the match has to allow for it. Every scope is
  // cleared, not just the active one: a write to Departments invalidates Departments, and which
  // company's copy is stale is not something the caller knows or should have to say.
  Array.from(store.keys()).forEach((key) => {
    if (key.startsWith(prefix) || key.includes(`|${prefix}`)) store.delete(key);
  });
}
